/**
 * Session History Store for Aim Assist Training Lab
 * Pure client-side local persistence with personal-best detection
 */

const HISTORY_KEY = 'kazakan_aim_history_v1';
const MAX_HISTORY = 30;

export class SessionHistory {
  constructor() {
    this.sessions = [];
    this.load();
  }

  load() {
    try {
      const saved = localStorage.getItem(HISTORY_KEY);
      if (saved) {
        this.sessions = JSON.parse(saved);
        if (!Array.isArray(this.sessions)) this.sessions = [];
      }
    } catch {
      this.sessions = [];
    }
  }

  save() {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(this.sessions));
    } catch {
      // LocalStorage error fallback
    }
  }

  /**
   * Adds a completed session record and checks if it beats previous personal best.
   */
  addSession(record) {
    const previousBest = this.getPersonalBest(record.mode);
    const isNewBest = !previousBest || record.score > previousBest.score;

    const entry = {
      id: 'ses_' + Date.now().toString(36),
      timestamp: Date.now(),
      mode: record.mode,
      gamePreset: record.gamePreset,
      score: Math.round(record.score || 0),
      accuracy: Math.round((record.accuracy || 0) * 10) / 10,
      hits: record.hits || 0,
      shots: record.shots || 0,
      reactionMs: record.reactionMs ? Math.round(record.reactionMs) : null,
      trackingPct: record.trackingPct ? Math.round(record.trackingPct * 10) / 10 : null,
      falseStarts: record.falseStarts || 0,
      durationSec: Math.round(record.durationSec || 0),
      isNewBest
    };

    this.sessions.unshift(entry);
    if (this.sessions.length > MAX_HISTORY) {
      this.sessions = this.sessions.slice(0, MAX_HISTORY);
    }
    this.save();
    return entry;
  }

  getPersonalBest(mode) {
    const modeSessions = this.sessions.filter((s) => s.mode === mode);
    if (modeSessions.length === 0) return null;
    return modeSessions.reduce((best, cur) => (cur.score > best.score ? cur : best), modeSessions[0]);
  }

  getRecentSessions(limit = 10) {
    return this.sessions.slice(0, limit);
  }

  getModeStats(mode) {
    const filtered = this.sessions.filter((s) => s.mode === mode);
    if (filtered.length === 0) return null;
    const avgScore = Math.round(filtered.reduce((sum, s) => sum + s.score, 0) / filtered.length);
    const avgAccuracy = Math.round((filtered.reduce((sum, s) => sum + s.accuracy, 0) / filtered.length) * 10) / 10;
    return {
      count: filtered.length,
      avgScore,
      avgAccuracy,
      best: this.getPersonalBest(mode)
    };
  }

  clear() {
    this.sessions = [];
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch {
      // LocalStorage removal fallback
    }
  }
}
